'use client'

import { ROUTES } from '@/config/routes'
import { ApiError } from '@/lib/api-client'
import { useRouter } from 'next/navigation'
import { useCallback, useState, useTransition } from 'react'
import { authService, type BackendUser } from '../services/auth-service'
import { useAuthStore } from '../stores/auth-store'
import { useRegistrationStore } from '../stores/registration-store'
import type { AuthUser, LoginCredentials, RegisterCredentials } from '../types'

interface AuthApiResponse {
	user: BackendUser
	accessToken: string
}

/**
 * Hook de autenticación.
 *
 * Flujo:
 *  1. Las rutas /api/auth/* (JWT propio en cookies httpOnly) manejan login/registro
 *  2. El perfil completo viene de la propia API (sinko_db.users)
 *
 * @example
 * ```tsx
 * const { login, register, logout, isLoading, error } = useAuth()
 * await login({ email: 'user@example.com', password: '...' })
 * ```
 */
export function useAuth() {
	const router = useRouter()
	const { setUser, setOnboardingCompleted, clearAuth } = useAuthStore()
	const { setStep, updateData: updateRegistrationData } = useRegistrationStore()
	const [error, setError] = useState<string | null>(null)
	const [isPending, startTransition] = useTransition()

	const clearError = useCallback(() => setError(null), [])

	/**
	 * Convierte los datos del backend propio a AuthUser para el store.
	 */
	const mapBackendUser = useCallback((backend: BackendUser): AuthUser => {
		return {
			id: backend.id,
			email: backend.email,
			role: backend.role,
			profile: {
				firstName: backend.firstName,
				lastName: backend.lastName,
				profileName: backend.profileName,
				avatarUrl: backend.avatarUrl ?? undefined,
			},
			avatarUrl: backend.avatarUrl,
			createdAt: backend.createdAt,
		}
	}, [])

	/**
	 * Sincroniza el usuario con la API propia (/api/auth/me).
	 * Obtiene el perfil completo desde sinko_db.
	 */
	const syncWithBackend = useCallback(async (): Promise<AuthUser | null> => {
		try {
			const backendUser = await authService.getMe()
			return mapBackendUser(backendUser)
		} catch {
			return null
		}
	}, [mapBackendUser])

	/**
	 * Iniciar sesión con email y contraseña.
	 */
	const login = useCallback(
		async (credentials: LoginCredentials) => {
			setError(null)

			let payload: AuthApiResponse
			try {
				const res = await fetch('/api/auth/login', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						email: credentials.email,
						password: credentials.password,
					}),
				})
				const raw = (await res.json().catch(() => ({}))) as Partial<AuthApiResponse> & {
					message?: string
				}

				if (!res.ok || !raw.user) {
					const message = raw.message ?? 'Credenciales inválidas'
					setError(message)
					return { success: false, error: message }
				}
				payload = raw as AuthApiResponse
			} catch {
				const message = 'Error de red al iniciar sesión'
				setError(message)
				return { success: false, error: message }
			}

			const backendUser = payload.user
			// Fuente de verdad: el backend calcula isOnboarded a partir de la
			// membresía primaria en sinko_db.company_members.
			const isOnboarded = backendUser.isOnboarded

			setUser(mapBackendUser(backendUser))
			setOnboardingCompleted(isOnboarded)

			// Notificar login al backend (métricas, last_login)
			authService.notifyLogin().catch(() => {
				// No bloquear el flujo por métricas
			})

			if (!isOnboarded) {
				// El usuario ya tiene cuenta → posicionar wizard en step 1
				setStep(1)
				updateRegistrationData({
					email: credentials.email,
					emailConfirmed: true,
					supabaseId: backendUser.id,
				})
			}

			startTransition(() => {
				router.push(isOnboarded ? ROUTES.DASHBOARD : ROUTES.REGISTER)
				router.refresh()
			})

			return { success: true }
		},
		[setUser, setOnboardingCompleted, mapBackendUser, router, setStep, updateRegistrationData]
	)

	/**
	 * Registrar nueva cuenta.
	 */
	const register = useCallback(
		async (credentials: RegisterCredentials) => {
			setError(null)

			let payload: AuthApiResponse
			try {
				const res = await fetch('/api/auth/signup', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						email: credentials.email,
						password: credentials.password,
						firstName: credentials.firstName,
						lastName: credentials.lastName,
					}),
				})
				const raw = (await res.json().catch(() => ({}))) as Partial<AuthApiResponse> & {
					message?: string
				}

				if (!res.ok || !raw.user) {
					const message = raw.message ?? 'Error al crear la cuenta'
					setError(message)
					return { success: false, error: message }
				}
				payload = raw as AuthApiResponse
			} catch {
				const message = 'Error de red al crear la cuenta'
				setError(message)
				return { success: false, error: message }
			}

			setUser(mapBackendUser(payload.user))

			startTransition(() => {
				router.push(ROUTES.ONBOARDING)
				router.refresh()
			})

			return { success: true }
		},
		[setUser, router, mapBackendUser]
	)

	/**
	 * Cerrar sesión.
	 */
	const logout = useCallback(async () => {
		await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {
			// Limpiar estado local aunque la red falle
		})
		clearAuth()
		window.location.href = ROUTES.LOGIN
	}, [clearAuth])

	/**
	 * Solicitar recuperación de contraseña.
	 * El servidor responde 501 mientras no exista servicio de email;
	 * ese mensaje se propaga al UI.
	 */
	const resetPassword = useCallback(async (email: string) => {
		setError(null)

		try {
			await authService.forgotPassword(email)
			return { success: true }
		} catch (err) {
			const message =
				err instanceof ApiError ? err.message : 'No se pudo solicitar la recuperación'
			setError(message)
			return { success: false, error: message }
		}
	}, [])

	/**
	 * Actualizar contraseña del usuario autenticado.
	 */
	const updatePassword = useCallback(
		async (newPassword: string) => {
			setError(null)

			try {
				await authService.changePassword(newPassword)
			} catch (err) {
				const message =
					err instanceof ApiError ? err.message : 'No se pudo actualizar la contraseña'
				setError(message)
				return { success: false, error: message }
			}

			startTransition(() => {
				router.push(ROUTES.DASHBOARD)
			})

			return { success: true }
		},
		[router]
	)

	return {
		login,
		register,
		logout,
		resetPassword,
		updatePassword,
		clearError,
		syncWithBackend,
		error,
		isLoading: isPending,
	}
}
